import { CustomResponse, UploadTemplate } from "../types/Grant";
import {
  BulkPaymentUploadPreview,
  BulkPaymentUploadResult,
} from "../types/BulkPaymentImport";
import { OfferState } from "../types/OptionBuyback";
import type {
  ApproveBankStatementRequest,
  BankStatementApprovalResult,
  BankStatementInspection,
  BankStatementMapper,
  BankStatementPreview,
  PreviewBankStatementRequest,
  SaveBankStatementMapperRequest,
} from "../types/Exercise";
import {
  AllocateSharesReq,
  BlackOutDay,
  EmployeeDetailsUploaded,
  EmployeeOfferDetails,
  OfferFilterV2,
  TransactionDetailsData,
  UploadedFile,
  WorkflowStepInformation,
} from "../types/Transaction";
import optionsApi from "./optionsApi";

export async function getTransactionDetails(
  id?: string,
  applyingOfferFilter = true
): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .get(`v2/transaction/getTransactionDetails`, {
      params: { id, applyingOfferFilter },
    })
    .then((res) => res.data);
}

export async function saveTransactionDetails(params: TransactionDetailsData) {
  return optionsApi
    .post(`v2/transaction/saveExerciseTransactionDetails`, params)
    .then((res) => res.data);
}

export async function deleteTransactionDetails() {
  return optionsApi.delete(`v2/transaction/deleteExerciseTransactionDetails`);
}

export async function getEmployeesFroAdditionToTransaction(): Promise<
  CustomResponse<EmployeeOfferDetails[]>
> {
  return optionsApi
    .get(`v2/transaction/getEmployeesForAddition`)
    .then((res) => res.data);
}

export async function createTransaction(
  data: TransactionDetailsData
): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .post(`v2/transaction/createTransaction`, data)
    .then((res) => res.data);
}

export async function startExerciseTransaction(data: {
  transactionId: string;
}): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .post(`v2/transaction/startTransaction`, data)
    .then((res) => res.data);
}

export async function closeExerciseTransaction(data: {
  transactionId: string;
}): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .post(`v2/transaction/closeTransaction`, data)
    .then((res) => res.data);
}

export async function completeExerciseTransaction(data: {
  transactionId: string;
}): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .post(`v2/transaction/completeTransaction`, data)
    .then((res) => res.data);
}

export async function deleteExerciseTransaction(
  transactionId: string
): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .delete(`v2/transaction/deleteTransaction/${transactionId}`)
    .then((res) => res.data);
}

export async function editTransaction(
  data: TransactionDetailsData
): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .put(`v2/transaction/editTransaction`, data)
    .then((res) => res.data);
}

export async function getPersonnelListUploadTemplate(): Promise<
  CustomResponse<UploadTemplate>
> {
  return optionsApi
    .get("v2/transaction/getPersonnelListUploadTemplate")
    .then((res) => res.data);
}

export async function validatePersonnelListFile(data: {
  file: string;
  existingTransaction: boolean;
}): Promise<CustomResponse<string[]>> {
  return optionsApi
    .post("v2/transaction/validatePersonnelListUploadTemplate", data)
    .then((res) => res.data);
}

export async function addEmployeeToTransaction(data: {
  employeesList: EmployeeDetailsUploaded[];
  transactionId: string;
  offerFilter: OfferFilterV2;
}): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .post(`v2/transaction/addEmployeeToTransaction`, data)
    .then((res) => res.data);
}

export async function deleteEmployeeFromTransaction(data: {
  employeeIds: string[];
  transactionId: string;
  offerFilter: OfferFilterV2;
}): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .post(`v2/transaction/deleteEmployeeFromTransaction`, data)
    .then((res) => res.data);
}

export async function createBlackOutDay(
  data: BlackOutDay
): Promise<CustomResponse<BlackOutDay>> {
  return optionsApi
    .post(`v2/blackOutDays/createBlackoutDay`, data)
    .then((res) => res.data);
}

export async function updateBlackOutDay(
  data: BlackOutDay
): Promise<CustomResponse<BlackOutDay>> {
  return optionsApi
    .put(`v2/blackOutDays/updateBlackoutDay`, data)
    .then((res) => res.data);
}

export async function updateBlackOutDayInMonetaryTransaction(data: {
  blackOutDays: string[];
  transactionId: string;
}): Promise<CustomResponse<BlackOutDay[]>> {
  return optionsApi
    .post(`v2/transaction/updateBlackOutDays`, data)
    .then((res) => res.data);
}

export async function deleteBlackOutDay(
  id: string
): Promise<CustomResponse<void>> {
  return optionsApi
    .delete(`v2/blackOutDays/deleteBlackoutDay`, { params: { id } })
    .then((res) => res.data);
}

export async function getBlackOutDaysForTransaction(): Promise<
  CustomResponse<BlackOutDay[]>
> {
  return optionsApi
    .get(`v2/blackOutDays/getAllBlackoutDays`)
    .then((res) => res.data);
}

export async function getAllExerciseEvents(): Promise<CustomResponse<any[]>> {
  return optionsApi
    .get(`v2/transaction/getAllExerciseEvents`)
    .then((res) => res.data);
}

export async function changeOfferStatus(data: {
  transactionId: string;
  employees: string[];
  requestIds?: string[]; // when present, scopes state change to these specific requestIds only
  status: OfferState;
  date?: Date;
  metaData?: any;
}): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .put(`v2/transaction/changeOfferState`, data)
    .then((res) => res.data);
}

export async function allocateSharesToEmployees(data: {
  employeeDetails: AllocateSharesReq[];
}): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .put(`v2/transaction/allocateShares`, data)
    .then((res) => res.data);
}

export async function getAllPendingExercisesForAllocation(data: {
  transactionIds: string[];
}): Promise<CustomResponse<EmployeeOfferDetails[]>> {
  return optionsApi
    .post(`v2/transaction/getAllPendingExercisesForAllocation`, data)
    .then((res) => res.data);
}

export async function uploadTransactionDocuments(data: {
  transactionId: string;
  employeeId: string;
  files: UploadedFile[];
}): Promise<CustomResponse<UploadedFile[]>> {
  return optionsApi
    .post(`v2/transaction/uploadTransactionDocuments`, data)
    .then((res) => res.data);
}

export async function getTransactionDocuments(data: {
  transactionId: string;
  employeeId: string;
}): Promise<CustomResponse<UploadedFile[]>> {
  return optionsApi
    .post(`v2/transaction/getExerciseTransactionDocuments`, data)
    .then((res) => res.data);
}

export async function getWillingnessInformation(data: {
  employeeId: string;
  eventId: string;
  requestId: string;
}): Promise<WorkflowStepInformation> {
  return optionsApi
    .get("v2/transaction/willingnessInformation", { params: data })
    .then((res) => res.data.data);
}

export interface ExerciseLetterAdminDoc {
  id: string;
  fileName: string;
  s3Url: string;
  version: number;
  entityId: string;
  entityType: string;
  signedByEmployee: boolean;
}

export async function getExerciseLettersAdmin(
  transactionId: string,
  employeeId: string,
  requestId?: string
): Promise<CustomResponse<ExerciseLetterAdminDoc[]>> {
  return optionsApi
    .get(`v2/transaction/getExerciseLettersAdmin`, {
      params: {
        transactionId,
        employeeId,
        ...(requestId ? { requestId } : {}),
      },
    })
    .then((res) => res.data);
}

export async function deleteTransactionDocuments(
  documentId: string
): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .delete(`v2/transaction/deleteExerciseTransactionDocuments`, {
      params: { documentId },
    })
    .then((res) => res.data);
}

export async function addExternalPaymentDetails(
  formData: FormData
): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .post(`v2/transaction/createExternalPayment`, formData)
    .then((res) => res.data);
}

export async function getBulkPaymentUploadTemplate(
  eventId: string
): Promise<Blob> {
  const response = await optionsApi.get(
    `v2/transaction/${eventId}/bulkPaymentTemplate`,
    { responseType: "blob" }
  );
  return response.data;
}

export async function previewBulkPaymentUpload(
  eventId: string,
  file: File
): Promise<CustomResponse<BulkPaymentUploadPreview>> {
  const formData = new FormData();
  formData.append("file", file);
  return optionsApi
    .post(`v2/transaction/${eventId}/previewBulkPayments`, formData)
    .then((response) => response.data);
}

export async function uploadBulkPaymentDetails(
  eventId: string,
  file: File
): Promise<CustomResponse<BulkPaymentUploadResult>> {
  const formData = new FormData();
  formData.append("file", file);
  return optionsApi
    .post(`v2/transaction/${eventId}/uploadBulkPayments`, formData)
    .then((response) => response.data);
}

export async function correctApprovedPayments(data: {
  transactionId: string;
  employeeId: string;
  requestId: string;
  correctionReason: string;
  paymentDetails: Array<{
    id?: string;
    updatedAt?: string;
    paymentType: "Exercise Price" | "Tax Price";
    referenceId: string;
    amount: number;
    currency?: string;
    paymentDate: string | Date;
    metaData?: Record<string, unknown>;
  }>;
}): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .post(`v2/transaction/correctApprovedPayments`, data)
    .then((res) => res.data);
}

export async function updatePaymentStatus(data: {
  transactionId: string;
  employeeId: string;
  status: string;
  paymentDetailIds: string[];
  requestId?: string;
  dematOverride?: { dematAccountId: string; type?: "NSDL" | "CDSL" };
  paymentOverride?: { referenceId?: string; paymentDate?: string };
}): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .post(`v2/transaction/updatePaymentStatus`, data)
    .then((res) => res.data);
}

export async function bulkApprovePaymentStatus(data: {
  transactionId: string;
  paymentType: "exercise price" | "tax price";
  status: "SUCCESS" | "FAILED";
  approvals: Array<{
    paymentDetailId: string;
    employeeId: string;
    requestId: string;
    dematOverride?: { dematAccountId: string; type?: "NSDL" | "CDSL" };
    paymentOverride?: { referenceId?: string; paymentDate?: string };
  }>;
}): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .post(`v2/transaction/bulkApprovePaymentStatus`, data)
    .then((res) => res.data);
}

export async function inspectBankStatement(
  file: File,
  headerRow?: number
): Promise<CustomResponse<BankStatementInspection>> {
  const formData = new FormData();
  formData.append("file", file);
  if (headerRow) formData.append("headerRow", String(headerRow));
  return optionsApi
    .post("v2/transaction/inspectBankStatement", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
}

export async function getBankStatementMappers(): Promise<
  CustomResponse<BankStatementMapper[]>
> {
  return optionsApi
    .get("v2/transaction/bankStatementMappers")
    .then((res) => res.data);
}

export async function saveBankStatementMapper(
  data: SaveBankStatementMapperRequest
): Promise<CustomResponse<BankStatementMapper>> {
  return optionsApi
    .post("v2/transaction/bankStatementMappers", data)
    .then((res) => res.data);
}

export async function previewBankStatement(
  data: PreviewBankStatementRequest
): Promise<CustomResponse<BankStatementPreview>> {
  const formData = new FormData();
  formData.append("file", data.file);
  formData.append("transactionId", data.transactionId);
  formData.append("mapping", JSON.stringify(data.mapping));
  return optionsApi
    .post("v2/transaction/previewBankStatement", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
}

export async function approveBankStatement(
  data: ApproveBankStatementRequest
): Promise<CustomResponse<BankStatementApprovalResult>> {
  return optionsApi
    .post("v2/transaction/approveBankStatement", data)
    .then((res) => res.data);
}

export async function sendReminderEmails({
  employeeIds,
  transactionId,
  templateType,
}: {
  employeeIds: string[];
  transactionId: string;
  templateType: string;
}): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .post(`v2/transaction/exerciseReminderEmails`, {
      employeeIds,
      transactionId,
      templateType,
    })
    .then((res) => res.data);
}

export async function getEmployeesFromExcel(
  file: string
): Promise<CustomResponse<any>> {
  return optionsApi
    .post(`v2/blackOutDays/getEmployeesFromExcel`, { file })
    .then((res) => res.data);
}

export async function resetEmployeeWillingness(data: {
  transactionId: string;
  employeeIds: string[];
  reason?: string;
  requestIds?: string[];
}): Promise<CustomResponse<TransactionDetailsData>> {
  return optionsApi
    .put(`v2/transaction/resetEmployeeWillingness`, data)
    .then((res) => res.data);
}
